import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import get from 'lodash/get';
import { useTranslation, Router } from '../../src/i18n';
import { colors, fontSize } from '../../constants/styleConstants';
import { profileDateConversion, getRouteWithSlug, swap, getOption } from '../../utils/common';
import StubPageSocialLinks from '../StubPageSocialLinks';
import usePermissions from '../../utils/permissions';
import NextLink from '../NextLink';
import routes from '../../constants/routes';
import RectangleButton from '../RectangleButton';
import { ARTIST_TAB_VALUES, TP, PRIVACY_ATTRIBUTE_IDS } from '../../constants/consts';
import ShareAccess from '../ShareAccess';
import DropdownButton from '../DropdownButton';
import ImageTag from '../ImageTag';
import TextButton from '../TextButton';
import ParseHtml from '../ParseHtml';
import H1 from '../H1';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    padding: '20px',
    fontFamily: 'Roboto',
    flexDirection: 'column',
  },
  profileSection: {
    display: 'flex',
  },
  name: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    width: '85%',
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'flex-start',
      marginLeft: '12px',
    },
    marginLeft: props => props.isBio && 20,
  },
  nameOnly: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    flexDirection: 'column',
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'flex-start',
      marginLeft: '12px',
    },
    marginLeft: props => props.isBio && 16,
    marginTop: props => props.isBio && 10,
  },
  artistImage: {
    width: '140px',
    height: '211px',
    objectFit: 'cover',
    objectPosition: 'top',
    marginRight: '20px',
    [theme.breakpoints.down('sm')]: {
      width: '100px',
      height: '180px',
      objectFit: 'cover',
    },
  },
  artistImageBio: {
    padding: '0px',
    margin: '0px',
    width: '75px',
    height: '101px',
    objectFit: 'cover',
    objectPosition: 'top',
    borderTopLeftRadius: '8px',
  },
  artistBio: {
    fontSize: fontSize.medium,
    color: '#626262',
    lineHeight: '1.64',
    overflow: 'hidden',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    '& span': {
      opacity: 0.5,
    },
  },
  artistBio1: {
    whiteSpace: props => (props.maxLines ? '' : 'pre-wrap'),
    fontSize: fontSize.medium,
    color: '#626262',
    lineHeight: '1.64',
    overflow: 'hidden',
    marginTop: '18px',
    paddingLeft: '15px',
    display: '-webkit-box', // Limiting artist bio with elipsises at the end
    '-webkit-line-clamp': props => props.maxLines || 99999,
    '-webkit-box-orient': 'vertical',
  },
  artistname: {
    fontSize: fontSize.h2,
    color: colors.grey3,
    fontWeight: 500,
    lineHeight: 'normal',
    [theme.breakpoints.down('sm')]: {
      fontSize: fontSize.h6,
    },
  },
  artistType: {
    fontSize: fontSize.h6,
    color: colors.grey3,
    lineHeight: 'normal',
    paddingTop: '7px',
    [theme.breakpoints.down('sm')]: {
      fontSize: fontSize.basic,
    },
  },

  artistDates: {
    fontSize: fontSize.medium,
    color: colors.grey3,
    lineHeight: 'normal',
    paddingTop: '7px',
    [theme.breakpoints.down('sm')]: {
      fontSize: fontSize.smallTitle,
    },
  },

  artistnameBio: {
    fontSize: fontSize.basic,
    color: colors.grey3,
    fontWeight: 500,
    lineHeight: 'normal',
    marginTop: '36px',
  },
  artistTypeBio: {
    fontSize: fontSize.smallTitle,
    color: colors.grey3,
    lineHeight: 'normal',
    opacity: 0.5,
    paddingTop: 4,
  },
  moreLink: {
    textTransform: 'lowercase',
    cursor: 'pointer',
    width: 'fit-content',
  },
  title: {
    fontWeight: 'bold',
  },
  rightIcons: {
    marginLeft: 'auto',
    display: 'flex',
  },
  shareButton: {
    marginBottom: 'auto',
    marginRight: 5,
  },
  editButton: {
    borderRadius: '8px',
    boxShadow: '0 2px 2px 0 rgba(9, 66, 135, 0.25)',
    backgroundColor: '#0067c5',
    minWidth: '67px',
    minHeight: '32px',
    padding: '4px 0',
    '&:hover': {
      backgroundColor: '#0067c5',
    },
  },
  bioInfo: {
    paddingLeft: '15px',
    paddingTop: '11px',
    paddingBottom: '48px',
  },
  bioDetails: {
    color: colors.grey3,
    lineHeight: '22px',
    fontSize: fontSize.medium,
    paddingTop: '12px',
  },
  langDropdownLarge: {
    position: 'absolute !important',
    right: '40px',
    paddingTop: '20px',
    width: '65px',
    '& .MuiInputBase-input': {
      padding: '5px 0 5px 10px !important',
      borderRadius: '7px  !important',
      border: 'solid 1px #dddddd !important',
      color: '#4b4b4b  !important',
      fontSize: '12px  !important',
      '& .MuiSvgIcon-root': {
        fill: `${colors.blueChip} !important`,
      },
    },
  },
}));

const ArtistInfoCard = ({ className, profile, bio, isBio, maxLines, lang, isLoggedIn }) => {
  const classes = useStyles({ maxLines, isBio });
  const { t } = useTranslation();
  const { artistPermissions } = usePermissions(profile?.id);
  let birthVisibility;
  let deathVisibility;
  let citizenShipVisibility;

  const { name, personal } = profile;
  const refContainer = useRef();
  // Visibility of bio details
  if (personal?.birth?.privacyAttribute?.id === PRIVACY_ATTRIBUTE_IDS.PRO) {
    birthVisibility = isLoggedIn;
  } else birthVisibility = true;

  if (personal?.death?.privacyAttribute?.id === PRIVACY_ATTRIBUTE_IDS.PRO) {
    deathVisibility = isLoggedIn;
  } else deathVisibility = true;

  if (personal?.citizenShip?.privacyAttribute?.id === PRIVACY_ATTRIBUTE_IDS.PRO) {
    citizenShipVisibility = isLoggedIn;
  } else citizenShipVisibility = true;

  const citizenShip = get(personal, 'citizenship', null);
  const profileDate = profileDateConversion(personal, lang, [
    'birth.date',
    'birth.city',
    'birth.country',
    'death.date',
    'death.city',
    'death.country',
  ]);

  const dateList = profileDate?.split('-');

  const artistProfession =
    get(profile, 'profession.qualification.name') || get(profile, 'profession.name') || get(profile, 'summary', '');

  const handleScroll = () => {
    window.scrollTo({
      top: refContainer.current.offsetHeight + 400,
      left: 0,
      behavior: 'smooth',
    });
  };

  const gotToTab = val => {
    handleScroll();
    Router.push(`${routes.ARTISTS}/[id]/[[...action]]`, `${getRouteWithSlug(routes.ARTISTS, profile)}/${val}`, {
      shallow: true,
    });
  };
  const contacts = get(profile, 'contacts', []);

  const [chosenLang, setLang] = React.useState(null);
  const [langOptions, setOptions] = React.useState([]);
  const [bioContent, setBio] = React.useState([]);
  const [showDropdown, setDropdownVisibility] = React.useState(true);
  React.useEffect(() => {
    if (isBio) {
      const data = bio?.filter(val => val.content != null);
      if (data?.length <= 0) {
        setDropdownVisibility(false);
      }
      const currentLangIndex = data?.findIndex(x => x.language.lang === lang);
      if (currentLangIndex > 0) {
        swap(data, 0, currentLangIndex);
      }
      const langsOptions = data?.map(b => ({ ...b, ...getOption(b?.language, 'id', 'lang') }));
      setBio(langsOptions[0]?.content);
      setOptions(langsOptions);
      setLang(langsOptions[0]);

      if (currentLangIndex === -1) setBio(profile?.description);
    } else {
      setBio(get(bio, 'content', '') || profile?.description);
      setDropdownVisibility(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bio]);

  const changelang = value => {
    setLang(value);
    setBio(value?.content);
  };

  const bioInformation = !isBio ? `<span>${t(`${TP}.BIO`)}:</span> ${bioContent}` : `${bioContent}`;
  const bioData = () => (
    <>
      <div className={isBio ? classes.artistBio1 : classes.artistBio}>
        <ParseHtml>{bioInformation}</ParseHtml>
      </div>
      {maxLines && (
        <TextButton className={classes.moreLink} onClick={() => gotToTab(ARTIST_TAB_VALUES.BIO)}>
          ...{t(`${TP}.MORE`)}
        </TextButton>
      )}
    </>
  );

  return (
    <div className={cn(classes.root, className)} ref={refContainer}>
      {isBio && showDropdown && (
        <div className={classes.langDropdownLarge}>
          <DropdownButton onChange={changelang} selectedValue={chosenLang} options={langOptions} />
        </div>
      )}
      <div className={classes.profileSection}>
        {!!get(profile, 'image.medium', false) && (
          <ImageTag
            src={get(profile, 'image.medium')}
            className={isBio ? classes.artistImageBio : classes.artistImage}
            alt={get(profile, 'name', 'artist photo')}
          />
        )}
        <div className={get(profile, 'image.medium', false) ? classes.name : classes.nameOnly}>
          <div className={isBio ? classes.artistnameBio : classes.artistname}>
            {isBio ? get(profile, 'name') : <H1 className={classes.artistname}>{get(profile, 'name')}</H1>}
          </div>
          <div className={isBio ? classes.artistTypeBio : classes.artistType}>{artistProfession}</div>
          {!isBio && (birthVisibility || deathVisibility) && <div className={classes.artistDates}>{profileDate}</div>}
          {
            !isBio && (
              <>
                {bioContent && (bioData())}
              </>
            )
          }
          {!isBio && (
            <StubPageSocialLinks
              contacts={contacts}
              showContact={get(profile, 'agencies', []).length > 0 || get(profile, 'contacts', []).length > 0}
              showContactsTab={() => gotToTab(ARTIST_TAB_VALUES.CONTACT)}
            />
          )}
        </div>
        <div className={classes.rightIcons}>
          {!isBio && artistPermissions.canEdit && (
            <ShareAccess className={classes.shareButton} entityType="profile" entity={profile} />
          )}
          {!isBio && artistPermissions.canEdit && (
            <NextLink href={`${routes.ARTISTS}/[id]/edit`} as={`${getRouteWithSlug(routes.ARTISTS, profile)}/edit`}>
              <RectangleButton btnText={t(`${TP}.m_EDIT`)} iconType="edit" />
            </NextLink>
          )}
        </div>
      </div>

      {
        isBio && (
          <>
            {bioContent && (bioData())}
          </>
        )
      }

      {isBio && (
        <div className={classes.bioInfo}>
          <>
            {birthVisibility && dateList && dateList[0] && (
              <div className={classes.bioDetails}>
                <span className={classes.title}>{t(`${TP}.BORN`)}: </span>
                {dateList.length > 0 && dateList[0]}
              </div>
            )}

            {deathVisibility && dateList && dateList[1] && (
              <div className={classes.bioDetails}>
                <span className={classes.title}>{t(`${TP}.DIED`)}: </span>
                {dateList.length > 1 && dateList[1]}
              </div>
            )}
          </>

          <div className={classes.bioDetails}>
            <span className={classes.title}>{t(`${TP}.FULL_NAME`)}: </span>
            {name}
          </div>
          {citizenShipVisibility && citizenShip?.countries && citizenShip?.countries.length > 0 && (
            <div className={classes.bioDetails}>
              <span className={classes.title}>{t(`${TP}.FN_NATIONALITY_LABEL`)}: </span>
              {citizenShip?.countries?.map(c => c?.name).join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ArtistInfoCard.propTypes = {
  className: PropTypes.string,
  profile: PropTypes.object,
  bio: PropTypes.any,
  isLoggedIn: PropTypes.bool,
};

export default ArtistInfoCard;
